import { Clock } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useWorkflowContext } from '../../contexts/WorkflowContext';
import { useNodeValidation, useNodeValidationPattern } from '../../hooks/useNodeValidation';
import { ValidationResultDisplay } from '../shared/ValidationResultDisplay';
import type { CustomNodeData, WaitNodeConfig } from '../../types/nodes';
import { VariablePicker } from '../VariablePicker';

interface WaitConfigProps {
  nodeId: string;
  data: CustomNodeData;
  onChange: (data: CustomNodeData) => void;
  // Wizard state callbacks for parent component
  onWizardStateChange?: (state: {
    wizardMode: boolean;
    currentStep: number;
    totalSteps: number;
    canProceedToNext: boolean;
    onPreviousStep: () => void;
    onNextStep: () => void;
    onCompleteWizard: () => Promise<void>;
    validationAttempted: boolean;
    validationFailed: boolean;
  }) => void;
  onValidationRequest?: (validate: () => Promise<boolean>) => void;
}

const defaultConfig: WaitNodeConfig = {
  delayMs: 5000
};

export function WaitConfig({ nodeId, data, onChange, onWizardStateChange, onValidationRequest }: WaitConfigProps) {
  // Validation pattern implementation
  const {
    validationAttempted,
    validationFailed,
    performValidation,
    handleCompleteWizard,
  } = useNodeValidationPattern(nodeId, data);
  
  // Validation display state
  const { isValidating, lastValidationResult } = useNodeValidation();

  const { getAvailableFields, getFlattenedFields } = useWorkflowContext();
  const [config, setConfig] = useState<WaitNodeConfig>(() => {
    const existingConfig = (data as any).config as Partial<WaitNodeConfig>;
    return {
      ...defaultConfig,
      ...existingConfig,
    };
  });
  const [waitType, setWaitType] = useState<'delay' | 'timestamp'>(() => {
    return config.resumeAt ? 'timestamp' : 'delay';
  });

  const availableFields = getAvailableFields(nodeId);
  const flattenedFields = getFlattenedFields(nodeId);

  const handleChange = (newConfig: Partial<WaitNodeConfig>) => {
    const updatedConfig = { ...config, ...newConfig };
    setConfig(updatedConfig);
    onChange({
      ...data,
      config: updatedConfig,
    } as CustomNodeData);
  };

  const handleTypeChange = (type: 'delay' | 'timestamp') => {
    setWaitType(type);
    if (type === 'delay') {
      handleChange({ resumeAt: undefined, delayMs: config.delayMs || 5000 });
    } else {
      handleChange({ delayMs: undefined, resumeAt: config.resumeAt || '' });
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
    return `${(ms / 3600000).toFixed(1)}h`;
  };

  // Register validation function with parent
  useEffect(() => {
    if (onValidationRequest) {
      onValidationRequest(performValidation);
    }
  }, [onValidationRequest, performValidation]);

  // Wizard state management
  useEffect(() => {
    if (onWizardStateChange) {
      onWizardStateChange({
        wizardMode: false, // Wait config doesn't use wizard mode
        currentStep: 1,
        totalSteps: 1,
        canProceedToNext: true,
        onPreviousStep: () => {},
        onNextStep: () => {},
        onCompleteWizard: async () => {
          await handleCompleteWizard(() => {
            // Save the current configuration
            onChange({
              ...data,
              config,
            } as CustomNodeData);
          });
        },
        validationAttempted,
        validationFailed,
      });
    }
  }, [onWizardStateChange, config, validationAttempted, validationFailed, handleCompleteWizard, onChange, data]);

  return (
    <div className="flex flex-col h-full">
      {/* Validation Results */}
      {(isValidating || lastValidationResult) && (
        <div className="px-6 py-4 bg-white border-b border-gray-200 dark:border-gray-700 dark:bg-gray-900">
          <ValidationResultDisplay
            errors={lastValidationResult?.errors || []}
            isValidating={isValidating}
          />
        </div>
      )}

      <div className="p-4 space-y-6">
        {/* Header */}
      <div>
        <h3 className="text-sm font-medium text-gray-900">Wait/Delay Configuration</h3>
        <p className="text-xs text-gray-500 mt-1">
          Pause workflow execution for a specific time or until a timestamp
        </p>
      </div>

      {/* Available Fields */}
      {availableFields.length > 0 && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Available Fields</h4>
          <div className="flex flex-wrap gap-1">
            {flattenedFields.slice(0, 10).map((field) => (
              <button
                key={field.id}
                onClick={() => {
                  navigator.clipboard.writeText(`{{${field.id}}}`);
                }}
                className="px-2 py-1 text-xs bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-50 transition-colors"
                title={`${field.description || field.label} - Click to copy {{${field.id}}}`}
              >
                {field.label}
              </button>
            ))}
            {flattenedFields.length > 10 && (
              <span className="text-xs text-blue-600">+{flattenedFields.length - 10} more</span>
            )}
          </div>
        </div>
      )}

      {/* Wait Type Selection */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">Wait Type</label>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleTypeChange('delay')}
            className={`p-3 border rounded-lg text-left transition-colors ${
              waitType === 'delay'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <div>
                <div className="font-medium text-sm">Fixed Delay</div>
                <div className="text-xs text-gray-500">Wait for a specific duration</div>
              </div>
            </div>
          </button>
          <button
            onClick={() => handleTypeChange('timestamp')}
            className={`p-3 border rounded-lg text-left transition-colors ${
              waitType === 'timestamp'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <div>
                <div className="font-medium text-sm">Until Timestamp</div>
                <div className="text-xs text-gray-500">Wait until specific time</div>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Configuration Fields */}
      {waitType === 'delay' ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Delay Duration <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={config.delayMs || 5000}
                onChange={(e) => handleChange({ delayMs: parseInt(e.target.value) || 5000 })}
                min="0"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={config.delayMs && config.delayMs >= 1000 ? (config.delayMs >= 60000 ? (config.delayMs >= 3600000 ? 'hours' : 'minutes') : 'seconds') : 'milliseconds'}
                onChange={(e) => {
                  const currentValue = config.delayMs || 5000;
                  let newValue = currentValue;
                  
                  switch (e.target.value) {
                    case 'milliseconds':
                      if (currentValue >= 1000) newValue = Math.floor(currentValue / 1000);
                      break;
                    case 'seconds':
                      if (currentValue < 1000) newValue = currentValue * 1000;
                      else if (currentValue >= 60000) newValue = Math.floor(currentValue / 1000);
                      break;
                    case 'minutes':
                      if (currentValue < 60000) newValue = currentValue * 60;
                      else if (currentValue >= 3600000) newValue = Math.floor(currentValue / 60000);
                      break;
                    case 'hours':
                      if (currentValue < 3600000) newValue = currentValue * 60;
                      break;
                  }
                  
                  handleChange({ delayMs: newValue });
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="milliseconds">ms</option>
                <option value="seconds">seconds</option>
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
              </select>
            </div>
            <div className="text-xs text-gray-500">
              Duration: {formatDuration(config.delayMs || 5000)}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Resume At Timestamp <span className="text-red-500">*</span>
          </label>
          <VariablePicker
            nodeId={nodeId}
            value={config.resumeAt || ''}
            onChange={(value) => handleChange({ resumeAt: value })}
            placeholder="2024-12-31T23:59:59Z or {{scheduled_time}}"
            className="w-full text-sm"
          />
          <div className="text-xs text-gray-500">
            Use ISO timestamp format or a variable containing a timestamp
          </div>
        </div>
      )}

      {/* Configuration Preview */}
      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <h5 className="text-sm font-medium text-gray-700 mb-2">Wait Behavior</h5>
        <div className="text-xs text-gray-600 space-y-1">
          {waitType === 'delay' ? (
            <>
              <div><strong>Type:</strong> Fixed Delay</div>
              <div><strong>Duration:</strong> {formatDuration(config.delayMs || 5000)}</div>
              <div className="mt-2 text-gray-500">
                Workflow will pause for {formatDuration(config.delayMs || 5000)} before continuing
              </div>
            </>
          ) : (
            <>
              <div><strong>Type:</strong> Until Timestamp</div>
              <div><strong>Resume At:</strong> {config.resumeAt || 'Not set'}</div>
              <div className="mt-2 text-gray-500">
                Workflow will pause until the specified timestamp is reached
              </div>
            </>
          )}
        </div>
      </div>

      {/* Output Structure */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <h5 className="text-sm font-medium text-blue-900 mb-2">Output Structure</h5>
        <div className="text-xs text-blue-800 space-y-1">
          <div>After wait completion, downstream nodes can access:</div>
          <div className="ml-4 space-y-1 font-mono">
            <div>{'{{wait.startedAt}}'} - When the wait started</div>
            <div>{'{{wait.resumedAt}}'} - When the wait ended</div>
            <div>{'{{wait.actualDuration}}'} - Actual wait duration in ms</div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
} 